// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Reads AYNI's mirror of the CelesTrak GP data on the stream host
 * (global/realtime/celestrak/, VPS handoff 2026-10-22). The client rules
 * from that handoff, all kept here:
 *
 * - read `latest.json` first; fetch a group only when its sha256 changed;
 * - check the sha256 (the worker does, on the bytes we hand it); a
 *   mismatch means we caught a file mid-update, so try again later;
 * - never more often than every 20 minutes (the data changes every 2 h);
 * - never call celestrak.org;
 * - treat data older than 6 h as stale.
 *
 * `satcat.json` and `events.json` are optional: the app works without
 * them (orbit grouping only, no events box).
 */
import { streamHostBase } from '../streamHost'
import { streamAuthFetch } from '../streamToken'
import type { GpGroupEntry, GpMirrorPointer, SatcatFile, SatelliteEventsFile, SatelliteSourcesFile } from './satelliteTypes'

export const FEED_PATH = 'global/realtime/celestrak/'
export const POLL_MS = 20 * 60 * 1000
export const STALE_MS = 6 * 3600 * 1000

export function feedBase(): string {
  return `${streamHostBase()}${FEED_PATH}`
}

export function isStale(updatedAt: string | undefined, now = Date.now()): boolean {
  const t = updatedAt ? Date.parse(updatedAt) : NaN
  return !Number.isFinite(t) || now - t > STALE_MS
}

export async function fetchPointer(fetcher: typeof fetch = streamAuthFetch): Promise<GpMirrorPointer> {
  const res = await fetcher(`${feedBase()}latest.json`, { cache: 'no-cache' })
  if (!res.ok) throw new Error(`HTTP ${res.status} for latest.json`)
  const json = (await res.json()) as GpMirrorPointer
  if (!json || typeof json !== 'object' || !json.groups) throw new Error('latest.json has no groups')
  return json
}

export async function fetchGroupBytes(entry: GpGroupEntry, fetcher: typeof fetch = streamAuthFetch): Promise<ArrayBuffer> {
  const res = await fetcher(new URL(entry.url, feedBase()).href)
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${entry.url}`)
  return res.arrayBuffer()
}

/** NORAD ids of a small group file (membership only). */
export async function fetchGroupIds(entry: GpGroupEntry, fetcher: typeof fetch = streamAuthFetch): Promise<Set<number>> {
  const res = await fetcher(new URL(entry.url, feedBase()).href)
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${entry.url}`)
  const rows = (await res.json()) as Array<{ NORAD_CAT_ID?: number }>
  return new Set(rows.map((r) => r.NORAD_CAT_ID).filter((n): n is number => typeof n === 'number'))
}

/** Where the pointer lists an extra file (`groups` or `extras`), or its default name. */
function extraUrl(pointer: GpMirrorPointer, key: string): string {
  const entry = pointer.extras?.[key] ?? pointer.groups[key]
  return new URL(entry?.url ?? `${key}.json`, feedBase()).href
}

async function fetchOptional<T>(url: string, fetcher: typeof fetch): Promise<T | null> {
  try {
    const res = await fetcher(url, { cache: 'no-cache' })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

export function fetchSatcat(pointer: GpMirrorPointer, fetcher: typeof fetch = streamAuthFetch): Promise<SatcatFile | null> {
  return fetchOptional<SatcatFile>(extraUrl(pointer, 'satcat'), fetcher).then((f) =>
    f && f.objects && typeof f.objects === 'object' ? f : null,
  )
}

export function fetchEvents(
  pointer: GpMirrorPointer,
  fetcher: typeof fetch = streamAuthFetch,
): Promise<SatelliteEventsFile | null> {
  return fetchOptional<SatelliteEventsFile>(extraUrl(pointer, 'events'), fetcher).then((f) =>
    f && Array.isArray(f.events) ? f : null,
  )
}

/**
 * `sources.json`, the satellites ↔ datasets index (optional). Without a
 * pointer (Browse asks before the layer has loaded anything) its default
 * place under the feed is tried.
 */
export function fetchSources(
  pointer: GpMirrorPointer | null,
  fetcher: typeof fetch = streamAuthFetch,
): Promise<SatelliteSourcesFile | null> {
  const url = pointer ? extraUrl(pointer, 'sources') : `${feedBase()}sources.json`
  return fetchOptional<SatelliteSourcesFile>(url, fetcher).then((f) =>
    f && f.platforms && typeof f.platforms === 'object' && f.datasets && typeof f.datasets === 'object' ? f : null,
  )
}
